import { useEffect, useState } from 'react';
import { Form, Radio, Select, Divider, Switch, Tooltip } from 'antd';
import { useSelector, useDispatch } from 'react-redux';
import {
  updateCurrentPractitionerSettings,
  updateCurrentPractitioner,
} from 'src/store/actions/practitioner.action';
import {
  sectionFormatOptions,
  assessmentFormatOptions,
  tooltips,
} from './config';
import { UpgradeTooltip } from '../Payments/UpgradeTooltip';
import {
  useAuth,
  useFeatureEntitlements,
  usePermissionEntitlements,
} from '@frontegg/react';
import { toast } from '@insight-health-ai/roseate/components';
import { InfoCircleOutlined } from '@ant-design/icons';
import './Settings.scss';
import { organizationSpecialties } from '../../store/actions/onboarding';
import { getCurrentPractitionerSettings } from '../../../../store/actions/practitioner.action';
import {
  getCreateEncountersOnBehalfOf,
  updateCreateEncountersOnBehalfOf,
} from '../../store/actions/settings';
import { useIsOP } from '../../../../hooks/misc';
import { analytics } from 'src/services';
import {
  getTimezone,
  postTimezone,
  updateTimezone,
} from 'src/store/actions/settings.action';
import ZoomGuidedTour from 'src/modules/common/components/ZoomGuidedTour';
import { useLocation } from 'react-router-dom';

const AmbientAISettings = () => {
  const [form] = Form.useForm();
  const { state: routerState } = useLocation();
  const { isEntitled: hasICD10Access } = useFeatureEntitlements('icd-10');
  const { isEntitled: hasCreateEncountersOnBehalfOfAccess } =
    useFeatureEntitlements('create-encounters-on-behalf-of');
  const { isEntitled: zoomEncountersAccess } =
    useFeatureEntitlements('zoom-encounters');
  const { isEntitled: isSidenavV2Entitled } =
    useFeatureEntitlements('sidenav-v2');
  const { isEntitled: canUpdateEncountersOnBehalfOf } =
    usePermissionEntitlements('encounter.on-behalf-of.settings');
  const { user } = useAuth();
  const isOP = useIsOP();
  const {
    getOnboardingSpecialties,
    currentPractitioner,
    currentPractitionerSettings,
    createEncountersOnBehalfOf,
    isCreateEncountersOnBehalfOfLoading,
  } = useSelector((state) => ({
    ...state?.onboardingState,
    ...state?.practitionerState,
    ...state?.settingsState,
  }));

  const { timezone, timezoneLoading } = useSelector(
    (state) => state?.appointmentSettingsState
  );

  const dispatch = useDispatch();

  const [localPractitionerSettings, setLocalPractitionerSettings] = useState(
    currentPractitionerSettings
  );
  const [selectedTimezone, setSelectedTimezone] = useState(null);

  const [localCreateEncountersOnBehalfOf, setLocalCreateEncountersOnBehalfOf] =
    useState(createEncountersOnBehalfOf);

  const highlightElementSelector = routerState?.highlightElementSelector;
  useEffect(() => {
    let timeout;
    if (highlightElementSelector) {
      timeout = setTimeout(() => {
        const element = document.querySelector(highlightElementSelector);
        if (element) {
          element.scrollIntoView({
            behavior: 'smooth',
          });
          element.classList.add('transition-colors', 'duration-500');
          setTimeout(() => {
            element.focus();
            element.classList.add('bg-primary-100');
          }, 100);
          setTimeout(() => {
            element.classList.remove('bg-primary-100');
          }, 1000);
        }
      }, 500);
    }
    return () => {
      clearTimeout(timeout);
    };
  }, [highlightElementSelector]);

  useEffect(() => {
    dispatch(getTimezone());
    dispatch(organizationSpecialties());
    dispatch(getCurrentPractitionerSettings());
    if (hasCreateEncountersOnBehalfOfAccess && canUpdateEncountersOnBehalfOf) {
      dispatch(getCreateEncountersOnBehalfOf());
    }
  }, [form]);

  useEffect(() => {
    if (timezone) {
      setSelectedTimezone(timezone);
      form.setFieldsValue({ timezone: timezone });
    }
  }, [timezone]);

  const handleTimezoneChange = (e) => {
    if (selectedTimezone == null) {
      dispatch(postTimezone({ timezone: e }));
    } else {
      dispatch(updateTimezone({ timezone: e }));
    }
    setSelectedTimezone(e);
  };

  useEffect(() => {
    if (!localPractitionerSettings) {
      setLocalPractitionerSettings(currentPractitionerSettings);
    }
  }, [currentPractitionerSettings]);

  useEffect(() => {
    if (!isCreateEncountersOnBehalfOfLoading) {
      setLocalCreateEncountersOnBehalfOf(createEncountersOnBehalfOf);
      form.setFieldsValue({
        create_encounters_on_behalf_of: createEncountersOnBehalfOf,
      });
    }
  }, [createEncountersOnBehalfOf, isCreateEncountersOnBehalfOfLoading]);

  const initialValues = {
    assessment_style:
      localPractitionerSettings?.note_preferences?.assessment_type,
    hpi_style: localPractitionerSettings?.note_preferences?.section_format?.hpi,
    subjective_style:
      localPractitionerSettings?.note_preferences?.section_format?.subjective,
    primary_specialty: currentPractitioner?.primary_specialty_id,
    sidenav_preferences_version:
      currentPractitionerSettings?.sidenav_preferences?.version,
  };

  const getToastMessage = (settingType) => {
    const messages = {
      assessment_type: 'Assessment style',
      hpi: 'HPI format',
      subjective: 'Subjective format',
      prefix_icd10: 'ICD-10 code preference',
      multi_lingual: 'Multilingual audio',
    };

    return `${messages[settingType] || settingType} has been updated`;
  };

  const handleSettingChange = (
    settingType,
    value,
    section = null,
    title = ''
  ) => {
    let updatedSettings;

    if (section) {
      updatedSettings = {
        note_preferences: {
          ...localPractitionerSettings?.note_preferences,
          section_format: {
            ...localPractitionerSettings?.note_preferences?.section_format,
            [section]: value,
          },
        },
      };
    } else {
      updatedSettings = {
        note_preferences: {
          ...localPractitionerSettings?.note_preferences,
          [settingType]: value,
        },
      };
    }

    setLocalPractitionerSettings(updatedSettings);

    dispatch(updateCurrentPractitionerSettings(updatedSettings));
    toast.success(getToastMessage(title || section || settingType));
  };

  return (
    <div className="mt-5">
      <div className="settings-component flex p-5 shadow-xl bg-white rounded-xl">
        <Form
          form={form}
          initialValues={initialValues}
          layout="horizontal"
          labelCol={{ span: 12 }}
          labelAlign="left"
          className="font-labels w-full"
          colon={false}
        >
          <h1 className="text-black font-bold text-[18px]">Note styling</h1>
          {/* Assessment Style Section */}
          <Form.Item
            label="Assessment Style"
            name="assessment_style"
            className="mb-6"
          >
            <Radio.Group
              onChange={(e) => {
                analytics.track(
                  `Changed assessment style to ${e.target.value} from settings`,
                  { email: user?.email }
                );
                handleSettingChange('assessment_type', e.target.value);
              }}
            >
              {assessmentFormatOptions.map((option) => (
                <div
                  key={option.value}
                  className="flex items-center gap-2 mb-2"
                >
                  <Radio value={option.value}>
                    <span>{option.label}</span>
                  </Radio>
                  <Tooltip
                    title={
                      tooltips.assessment[option.value] ||
                      tooltips.assessment.title
                    }
                    placement="right"
                  >
                    <InfoCircleOutlined
                      style={{ fontSize: '16px' }}
                      className="text-gray-400 cursor-help"
                    />
                  </Tooltip>
                </div>
              ))}
            </Radio.Group>
          </Form.Item>
          {/* Section Format Settings */}
          {sectionFormatOptions.map((section) => (
            <Form.Item
              key={section.type}
              label={`${section.title} Style`}
              name={`${section.type}_style`}
              className="mb-6"
            >
              <Radio.Group
                onChange={(e) => {
                  analytics.track(
                    `Changed ${section.title} style to ${e.target.value} from settings`,
                    { email: user?.email }
                  );
                  handleSettingChange(
                    section.type,
                    e.target.value,
                    section.type
                  );
                }}
              >
                {section.options.map((option) => (
                  <div
                    key={option.value}
                    className="flex items-center gap-2 mb-2"
                  >
                    <Radio value={option.value}>
                      <span>{option.label}</span>
                    </Radio>
                    <Tooltip
                      title={tooltips.sectionFormat[option.value]}
                      placement="right"
                    >
                      <InfoCircleOutlined
                        style={{ fontSize: '16px' }}
                        className="text-gray-400 cursor-help"
                      />
                    </Tooltip>
                  </div>
                ))}
              </Radio.Group>
            </Form.Item>
          ))}
          {/* ICD-10 Switch */}
          <Form.Item
            label={
              <div className="flex items-center gap-2">
                <span>Include ICD-10 codes in notes</span>
                <Tooltip title={tooltips.icd10.title} placement="right">
                  <InfoCircleOutlined
                    style={{ fontSize: '16px' }}
                    className="text-gray-400 cursor-help"
                  />
                </Tooltip>
              </div>
            }
            name="prefix_icd10"
            className="mb-6"
          >
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-2">
                {hasICD10Access ? (
                  <Switch
                    defaultChecked={
                      localPractitionerSettings?.note_preferences?.prefix_icd10
                    }
                    onChange={(checked) => {
                      analytics.track(
                        `${
                          checked ? 'Enabled' : 'Disabled'
                        } include ICD-10 codes in notes from settings`,
                        { email: user?.email }
                      );
                      handleSettingChange('prefix_icd10', checked);
                    }}
                  />
                ) : (
                  <UpgradeTooltip>
                    <Switch
                      disabled
                      defaultChecked={false}
                      className="bg-gray-200"
                    />
                  </UpgradeTooltip>
                )}
              </div>
            </div>
          </Form.Item>
          <Divider />
          <h1 className="text-black font-bold text-[18px]">General</h1>
          {/* Specialty Select */}
          {!isOP && (
            <Form.Item
              label={
                <div className="flex items-center gap-2">
                  <span>Specialty</span>
                  <Tooltip title={tooltips.specialty.title} placement="right">
                    <InfoCircleOutlined
                      style={{ fontSize: '16px' }}
                      className="text-gray-400 cursor-help"
                    />
                  </Tooltip>
                </div>
              }
              name="primary_specialty"
              className="mb-6"
            >
              <Select
                placeholder="Select your primary specialty"
                options={getOnboardingSpecialties?.map((item) => ({
                  label: item.specialty_name,
                  value: item.specialty_id,
                }))}
                onChange={(value, option) => {
                  analytics.track(
                    `Changed primary specialty to ${option.label} from settings`,
                    { email: user?.email }
                  );
                  dispatch(
                    updateCurrentPractitioner({ primary_specialty_id: value })
                  );
                  toast.success('Your specialty has been updated');
                }}
              />
            </Form.Item>
          )}
          {/* Multi-lingual Support Switch */}
          <Form.Item
            label={
              <div className="flex items-center gap-2">
                <span>Multilingual Audio</span>
                <Tooltip
                  title="Enable enhanced audio transcription to support non-English languages"
                  placement="right"
                >
                  <InfoCircleOutlined
                    style={{ fontSize: '16px' }}
                    className="text-gray-400 cursor-help"
                  />
                </Tooltip>
              </div>
            }
            name="multi_lingual"
            className="mb-6"
          >
            <div className="flex items-center gap-2">
              <Switch
                defaultChecked={
                  localPractitionerSettings?.note_preferences?.multi_lingual
                }
                onChange={(checked) => {
                  analytics.track(
                    `${
                      checked ? 'Enabled' : 'Disabled'
                    } multi-lingual audio from settings`,
                    { email: user?.email }
                  );
                  handleSettingChange('multi_lingual', checked);
                }}
              />
            </div>
          </Form.Item>
          {/* Timezone Select */}
          <Form.Item
            label={
              <div className="flex items-center gap-2">
                <span>Timezone</span>
                <Tooltip title={'Select your timezone'} placement="right">
                  <InfoCircleOutlined
                    style={{ fontSize: '16px' }}
                    className="text-gray-400 cursor-help"
                  />
                </Tooltip>
              </div>
            }
            name="timezone"
            className="mb-6"
          >
            <Select
              placeholder="Select timezone"
              options={[
                { value: 'US/Eastern', label: 'US/Eastern' },
                { value: 'US/Central', label: 'US/Central' },
                {
                  value: 'US/Mountain',
                  label: 'US/Mountain',
                },
                { value: 'US/Pacific', label: 'US/Pacific' },
                { value: 'US/Alaska', label: 'US/Alaska' },
                { value: 'US/Hawaii', label: 'US/Hawaii' },
                { value: 'America/Puerto_Rico', label: 'US/Atlantic' },
                { value: 'Pacific/Pago_Pago', label: 'US/Samoa' },
                { value: 'Pacific/Guam', label: 'US/Chamorro' },
                {
                  value: 'Asia/Kolkata',
                  label: 'Asia/Kolkata',
                },
              ]}
              loading={timezoneLoading}
              value={selectedTimezone || undefined}
              onChange={(value, option) => {
                analytics.track(
                  `Changed timezone to ${option.label} from settings`,
                  { email: user?.email }
                );
                handleTimezoneChange(value);
              }}
            />
          </Form.Item>

          {zoomEncountersAccess && (
            <Form.Item
              label={
                <div className="flex items-center gap-2">
                  <span>Show Zoom Icon</span>
                  <Tooltip
                    title="AI bot joins Zoom calls to automatically record and transcribe patient encounters."
                    placement="right"
                  >
                    <InfoCircleOutlined
                      style={{ fontSize: '16px' }}
                      className="text-gray-400 cursor-help"
                    />
                  </Tooltip>
                </div>
              }
              name="enable_zoom_integration"
              className="mb-6"
            >
              <div className="flex items-center gap-2">
                <Switch
                  defaultChecked={
                    localPractitionerSettings?.note_preferences
                      ?.enable_zoom_integration
                  }
                  onChange={(checked) => {
                    analytics.track(
                      `${
                        checked ? 'Enabled' : 'Disabled'
                      } Zoom integration from settings`,
                      { email: user?.email }
                    );
                    handleSettingChange(
                      'enable_zoom_integration',
                      checked,
                      null,
                      'Enable Zoom Integration'
                    );
                  }}
                />
                <ZoomGuidedTour />
              </div>
            </Form.Item>
          )}
          {hasCreateEncountersOnBehalfOfAccess &&
            canUpdateEncountersOnBehalfOf && (
              <Form.Item
                label={
                  <div className="flex items-center gap-2">
                    <span>Create encounters on behalf of</span>
                    <Tooltip
                      title="Enable to be able to create encounters on behalf of other users"
                      placement="right"
                    >
                      <InfoCircleOutlined
                        style={{ fontSize: '16px' }}
                        className="text-gray-400 cursor-help"
                      />
                    </Tooltip>
                  </div>
                }
                name="create_encounters_on_behalf_of"
                className="mb-6"
              >
                <div className="flex items-center gap-2">
                  <Switch
                    defaultChecked={localCreateEncountersOnBehalfOf}
                    onChange={(checked) => {
                      analytics.track(
                        `${
                          checked ? 'Enabled' : 'Disabled'
                        } create encounters on behalf of from settings`,
                        { email: user?.email }
                      );
                      dispatch(
                        updateCreateEncountersOnBehalfOf({
                          create_encounters_on_behalf: checked,
                        })
                      );
                    }}
                  />
                </div>
              </Form.Item>
            )}
          {isSidenavV2Entitled && (
            <Form.Item
              label={
                <div className="flex items-center gap-2">
                  <span>Switch to new layout</span>
                  <Tooltip
                    title="Enable to switch to the new layout"
                    placement="right"
                  >
                    <InfoCircleOutlined
                      style={{ fontSize: '16px' }}
                      className="text-gray-400 cursor-help"
                    />
                  </Tooltip>
                </div>
              }
              name="sidenav_preferences_version"
              className="mb-6 layout-version-switcher"
            >
              <div className="flex items-center gap-2">
                <Switch
                  defaultChecked={
                    initialValues?.sidenav_preferences_version === 2
                  }
                  onChange={(checked) => {
                    analytics.track(
                      `${
                        checked ? 'Enabled' : 'Disabled'
                      } New Layout in Settings`,
                      { email: user?.email }
                    );
                    dispatch(
                      updateCurrentPractitionerSettings({
                        sidenav_preferences: {
                          ...currentPractitionerSettings?.sidenav_preferences,
                          version: checked ? 2 : 1,
                        },
                      })
                    )
                      .then(() => {
                        window.location.reload();
                      })
                      .catch(() => {
                        toast.error('Failed to switch to new layout');
                      });
                  }}
                />
              </div>
            </Form.Item>
          )}
        </Form>
      </div>
    </div>
  );
};

export default AmbientAISettings;
